/** @jsx jsx */
import { jsx } from "theme-ui"
import { useEffect } from "react"
import Link from "@components/Link"
import { connect } from "react-redux"
import { navigate } from "@reach/router"
import styled from "styled-components"
import FocusLock from "react-focus-lock"

import Nav from "@components/Nav"
import theme, { rem } from "@src/theme"
import ButtonLink from "@components/ButtonLink"
import useWindowSize from "@helpers/hooks/useWindowSize"
import {
  Accordion as LibraryAccordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion"
import "react-accessible-accordion/dist/fancy-example.css"
import { graphql, useStaticQuery } from "gatsby"

const Panel = ({ title, children, onClose }) => {
  return (
    <PanelStyleWrapper
      hasChildren={children && children.length > 0 ? true : false}
    >
      <AccordionItem
        uuid={children && children.length ? `has-children-${title.replaceAll(/\s/g, '')}` : title.replaceAll(/\s/g, '')}
      >
        <AccordionItemHeading>
          <AccordionItemButton>{title}</AccordionItemButton>
        </AccordionItemHeading>
        {children ? (
          <AccordionItemPanel>
            <ul
              className="nav-links-list"
              sx={{
                listStyleType: "none",
              }}
            >
              {children.map(({ title, url }) => (
                <li key={title}>
                  <Link
                    to={url}
                    style={{
                      fontSize: `${rem(18)}!important`,
                      width: "100%",
                      display: "block",
                      pl: rem(22),
                    }}
                    onClick={onClose}
                  >
                    {title}
                  </Link>
                </li>
              ))}
            </ul>
          </AccordionItemPanel>
        ) : null}
      </AccordionItem>
    </PanelStyleWrapper>
  )
}

const Accordion = ({ onClose, items }) => {
  const onChangeHandler = panel => {
    if (panel && panel[0]) {
      if (!panel[0].includes("has-children")) {
        const item = items.find(({ title }) => title === panel[0])
        if (item.url) {
          navigate(item.url)
        } else if (item.onClick) {
          item.onClick()
        }

        onClose()
      }
    }
  }
  return (
    <AccordionStylesWrapper>
      <LibraryAccordion allowZeroExpanded onChange={onChangeHandler}>
        {items.map((item, index) => (
              <Panel {...item} onClose={onClose} key={index} />
          ))}
      </LibraryAccordion>
    </AccordionStylesWrapper>
  )
}

const navObjects = [
  {
    title: "Shop",
    url: "/shop",
  },
  {
    title: "What is IDAHOBIT",
    url: "",
    children: [
      { title: "May 17", url: "/about-may-17" },
      { title: "Our Impact", url: "/our-impact" }
    ],
  },
  {
    title: "Explore inclusion",
    url: "",
    children: [
      { title: "Identity and Stats", url: "/identity-and-stats" },
      { title: "People and Stories", url: "/people-and-stories" },
      { title: "Allyship", url: "/allyship" },
    ],
  },
  {
    title: "Get involved",
    url: "",
    children: [
      { title: "Get Started", url: "/get-started" },
      { title: "Host an Activity", url: "/host-an-activity" },
      { title: "Training and Speakers", url: "/training-and-speakers" },
      { title: "Resources", url: "/resources" },
      { title: "Fundraising", url: "/fundraising" },
      { title: "Partner", url: "/partner" },
    ],
  }
]

const navObjectsIDAHOBITDay = [
  {
    title: "Shop",
    url: "/shop",
  },
  {
    title: "What is IDAHOBIT",
    url: "",
    children: [
      { title: "May 17", url: "/about-may-17" },
      { title: "Our Impact", url: "/our-impact" }
    ],
  },
  {
    title: "Explore inclusion",
    url: "",
    children: [
      { title: "Identity and Stats", url: "/identity-and-stats" },
      { title: "People and Stories", url: "/people-and-stories" },
      { title: "Allyship", url: "/allyship" },
    ],
  },
  {
    title: "Get involved",
    url: "",
    children: [
      { title: "Get Started", url: "/get-started" },
      { title: "Host an Activity", url: "/host-an-activity" },
      { title: "Training and Speakers", url: "/training-and-speakers" },
      { title: "Resources", url: "/resources" },
      { title: "Fundraise", url: "/fundraise" },
      { title: "Partner", url: "/partner" },
    ],
  },
  {
    title: "Register",
    url: "/register",
  }
]

const MobileNav = ({ navOpen, closeNav, openDonationModal }) => {
  const windowSize = useWindowSize()
  useEffect(() => {
    if (windowSize.width >= 1200 && navOpen) {
      closeNav()
    }
  }, [windowSize])

  const data = useStaticQuery(
    graphql`
      query {
        pageData: markdownRemark(
          fileAbsolutePath: { glob: "**/globals/IDAHOBIT.md" }
        ) {
          frontmatter {
            idahobitDay
          }
        }
      }
    `
  )

  const { idahobitDay } = data.pageData.frontmatter

  let items = idahobitDay ? navObjectsIDAHOBITDay : navObjects
  items = [...items, {
    title: "Donate",
    onClick: openDonationModal
  }]

  return navOpen ? (
    <div
      sx={{
        position: "fixed",
        left: "6px",
        top: "6px",
        height: "calc(100vh - 12px)",
        width: "calc(100% - 12px)",
        zIndex: "11",
        backgroundColor: "white",
        overflow: "auto",
      }}
    >
      <FocusLock>
        <Nav />
        <Accordion items={items} onClose={() => closeNav()} />
        <div
          sx={{
            px: ["4vw", "2.5vw"],
            pb: 4,
          }}
        >
          <ButtonLink
            styles={{ width: "100%", mt: rem(20) }}
            to={idahobitDay ? '/donate' : "/register"}
            external
          >
            {idahobitDay ? ("Donate") : ("Register")}
          </ButtonLink>
        </div>
      </FocusLock>
    </div>
  ) : null
}

export default connect(
  state => ({
    navOpen: state.ui.navOpen,
  }),
  dispatch => ({
    closeNav: dispatch.ui.closeNav,
    openDonationModal: () => dispatch.donationModal.toggleDonationModal(true),
  })
)(MobileNav)

const AccordionStylesWrapper = styled.div`
  font-size: ${rem(18)};

  .accordion {
    border: none;
  }
  .accordion__button {
    font-family: ${theme.fonts.heading};
    color: ${theme.colors.darkPurple};
    background: ${theme.colors.white};
    font-weight: 900;

    &:hover {
      background-color: transparent;
    }
  }
  .accordion__button[aria-expanded="true"] {
    color: ${theme.colors.purple};
  }
  .accordion__button:after {
    display: inline-block;
    content: "";
    height: 10px;
    width: 10px;
    margin-right: 12px;
    border-bottom: 3px solid ${theme.colors.darkPurple};
    border-right: 3px solid ${theme.colors.darkPurple};
    transform: rotate(-45deg) translate(-4px, 10px);
    text-align: right;
    float: right;
    transition: transform 300ms ease;
  }

  .accordion__button[aria-expanded="true"]::after,
  .accordion__button[aria-selected="true"]::after {
    transform: rotate(45deg) translate(-4px, 10px);
    border-color: ${theme.colors.purple};
  }

  .accordion__button:before {
    display: none;
  }
`

const PanelStyleWrapper = styled.div`
  .accordion__button {
    /* text-transform: uppercase; */
    padding-top: ${rem(20)}!important;
    padding-bottom: ${rem(20)}!important;
    @media screen and (max-height: 850px) {
      padding-top: ${rem(15)}!important;
      padding-bottom: ${rem(15)}!important;
    }

    @media screen and (max-height: 700px) {
      padding-top: ${rem(12)}!important;
      padding-bottom: ${rem(12)}!important;
    }

    @media only screen and (max-width: ${theme.breakpoints[0]}) {
      padding-left: 4vw;
      padding-right: 4vw;
    }
    @media only screen and (min-width: ${theme.breakpoints[0]}) {
      padding-left: 2.5vw;
      padding-right: 2.5vw;
    }
  }

  .accordion__button:after {
    display: ${p => (p.hasChildren ? "inline-block" : "none")};
  }

  .accordion__panel {
    @media only screen and (max-width: ${theme.breakpoints[0]}) {
      padding: 0 4vw 0 4vw;
    }
    @media only screen and (min-width: ${theme.breakpoints[0]}) {
      padding: 0 2.5vw 0 2.5vw;
    }
  }

  .nav-links-list {
    li {
      margin-bottom: ${rem(20)};
    }
  }
`
